import React, { useState } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Button, Modal, ModalBody } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import { changeEmail as changeEmailAction } from 'Redux/ducks/auth';
import MessageBox from 'Components/Utility/MessageBox';
import Captcha from 'Containers/Captcha/Captcha';

const messages = defineMessages({
  enter_email: {
    id: 'account-email.message.enter-email',
    defaultMessage: 'Please enter an email address',
  },
  confirm_email: {
    id: 'account-email.message.confirm-email',
    defaultMessage: 'We have sent you a confirmation email to {email}. Please check your email.',
  },
  captcha: {
    id: 'account-email.message.captcha',
    defaultMessage: 'Please tick the ReCaptcha Tick Box above.',
  },
});

const AccountEmail = ({ emailAddress, changeEmail, intl }) => {
  const [changing, setChanging] = useState(false);
  const [newemail, setNewemail] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailFail, setEmailFail] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCloseButton, setShowCloseButton] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(false);


  const { formatMessage } = intl;

  const resetMessageTimer = () => {
    const displayTime = 1000 * 6;
    setTimeout(() => {
      setEmailSuccess(false);
      setEmailFail(false);
      setEmailError('');
      setLoading(false);
    }, displayTime);
  };


  const handleChange = (e) => {
    setNewemail(e.target.value);
  };

  const submitChangeEmail = async (newEmail, email) => {
    try {
      await changeEmail(newEmail, email, captchaToken);
      setEmailSuccess(true);
      setShowCloseButton(true);
      resetMessageTimer();
    } catch (ex) {
      setEmailFail(true);
      setEmailError(ex.message);
      setLoading(false);
      resetMessageTimer();
    }
  };

  const click = (submit) => {
    if (!submit) {
      setNewemail('');
      setEmailSuccess(false);
      setEmailFail(false);
      setEmailError('');
      setChanging(!changing);
      setLoading(false);
      return;
    }
    if (newemail === '') {
      setEmailFail(true);
      setEmailError(formatMessage(messages.enter_email));
      return;
    }
    if (!captchaToken) {
      setEmailFail(true);
      setEmailError(formatMessage(messages.captcha));
      return;
    }
    setEmailSuccess(false);
    setEmailFail(false);
    setEmailError('');
    setLoading(true);
    submitChangeEmail(newemail, emailAddress);
  };

  const captchaCallback = (token) => {
    setCaptchaToken(token);
  };


  return (
    <React.Fragment>
      <div className="account-table-row">
        <div className="account-table-row-title">
          <FormattedMessage id="account-email.table.title" defaultMessage="Email Address" />
        </div>
        <div className="account-table-row-content">
          <div className="account-table-details-container">
            {emailAddress}
          </div>
          <div className="account-table-action-container">
            <button className="account-table-action-button" onClick={() => click(false)}>
              <FormattedMessage id="account-email.table.button.email" defaultMessage="Change Email Address" />
            </button>
          </div>
        </div>
      </div>

      <Modal isOpen={changing} toggle={() => click(false)} className="account-modal">
        <ModalBody>
          <div className="account-modal-title">
            <FormattedMessage id="account-email.modal.title" defaultMessage="Change Email Address" />
          </div>

          {changing &&
            <div className="account-modal-form">
              <div className="form-group">
                <label htmlFor="newemail" className="account-modal-form-title">
                  <FormattedMessage id="account-email.form.newemail" defaultMessage="New Email Address:" />
                </label>
                <div className="account-modal-form-input">
                  <input
                    id="newemail"
                    aria-label="new-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    name="newemail"
                    value={newemail}
                    className={classNames('form-control', { 'input-error': emailFail })}
                    onChange={handleChange}
                  />
                  <Captcha name="account-email-recaptcha" onChange={captchaCallback} />
                  {emailFail &&
                    <div className="invalid-field">
                      {emailError}
                    </div>
                  }
                </div>
              </div>

              {emailSuccess &&
                <MessageBox
                  type="success"
                  title="Email Changed"
                  message={formatMessage(messages.confirm_email, { email: emailAddress })}
                />
              }
              <div className="account-modal-form-buttons">
                {!loading && !emailSuccess && !showCloseButton &&
                  <React.Fragment>
                    <div className="form-button-container">
                      <Button color="primary" onClick={() => click(true)}>
                        <FormattedMessage id="account-email.form.confirm" defaultMessage="Confirm" />
                      </Button>
                    </div>
                    <div className="form-button-container">
                      <Button color="secondary" onClick={() => click(false)}>
                        <FormattedMessage id="account-email.form.cancel" defaultMessage="Cancel" />
                      </Button>
                    </div>
                  </React.Fragment>
                }
                {loading && !emailSuccess &&
                  <div>
                    <FontAwesomeIcon spin icon={faSpinner} title="Loading" />
                  </div>
                }
                {showCloseButton &&
                  <div className="form-button-container">
                    <Button color="secondary" onClick={() => click(false)}>
                      <FormattedMessage id="account-email.form.close" defaultMessage="Close" />
                    </Button>
                  </div>
                }
              </div>
            </div>
          }
        </ModalBody>

        <div className="close-button-container">
          <button aria-label="close" onClick={() => click(false)}>
            <FontAwesomeIcon icon={faXmark} title="Close modal" />
          </button>
        </div>
      </Modal>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  auth: { email: emailAddress },
}) => ({
  emailAddress,
});

const mapDispatchToProps = { changeEmail: changeEmailAction };

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(AccountEmail));
